import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Moon, Sun, Type, Shield, HelpCircle, Edit2, Check, X, Smartphone, Key } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import { resetOnboarding } from '../components/Onboarding';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, checkAuth, logout } = useAuth();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [fontSize, setFontSize] = useState(localStorage.getItem('fontSize') || 'medium');
  const [notifications, setNotifications] = useState(localStorage.getItem('notifications') !== 'false');
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.full_name || '');
  const [isSavingName, setIsSavingName] = useState(false);
  const [clearingSessions, setClearingSessions] = useState(false);
  const [deviceCount, setDeviceCount] = useState(0);
  const [activationCode, setActivationCode] = useState('');
  const [activatingSubscription, setActivatingSubscription] = useState(false);

  useEffect(() => {
    applyTheme(theme);
    applyFontSize(fontSize);
    fetchDeviceCount();
  }, [theme, fontSize]);

  const fetchDeviceCount = async () => {
    try {
      const response = await api.get('/users/me/device-count');
      setDeviceCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch device count:', error);
    }
  };

  const applyTheme = (selectedTheme) => {
    if (selectedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', selectedTheme);
  };

  const applyFontSize = (size) => {
    const root = document.documentElement;
    switch (size) {
      case 'small':
        root.style.fontSize = '14px';
        break;
      case 'large':
        root.style.fontSize = '18px';
        break;
      default:
        root.style.fontSize = '16px';
    }
    localStorage.setItem('fontSize', size);
  };

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    setIsSavingName(true);
    try {
      await api.put('/auth/update-profile', { full_name: newName.trim() });
      
      // Refresh user data from backend using checkAuth
      await checkAuth();
      
      setIsEditingName(false);
      toast.success('Name updated successfully');
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to update name');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleCancelEdit = () => {
    setNewName(user?.full_name || '');
    setIsEditingName(false);
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
    toast.success(`Theme changed to ${newTheme}`);
  };

  const handleFontSizeChange = (newSize) => {
    setFontSize(newSize);
    applyFontSize(newSize);
    toast.success(`Font size changed to ${newSize}`);
  };

  const handleNotificationsToggle = (enabled) => {
    setNotifications(enabled);
    localStorage.setItem('notifications', enabled);
    toast.success(`Notifications ${enabled ? 'enabled' : 'disabled'}`);
  };

  const handleResetOnboarding = () => {
    resetOnboarding();
    toast.success('Onboarding tour reset! Refresh the page to see the tour again.');
  };

  const handleClearSessions = async () => {
    if (!window.confirm('Clear all active sessions? You will be logged out immediately and need to log in again.')) {
      return;
    }

    setClearingSessions(true);
    try {
      await api.delete('/users/me/sessions');
      toast.success('All sessions cleared! Logging you out...');
      
      // Wait a moment for the user to see the message
      setTimeout(async () => {
        await logout();
        navigate('/');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear sessions');
      setClearingSessions(false);
    }
  };

  const handleActivateSubscription = async (e) => {
    e.preventDefault();
    if (!activationCode.trim()) {
      toast.error('Please enter an activation code');
      return;
    }

    setActivatingSubscription(true);
    try {
      const response = await api.post('/auth/activate-subscription', { 
        activation_code: activationCode.trim().toUpperCase() 
      });
      toast.success(response.data.message);
      setActivationCode('');
      // Refresh auth state
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid activation code');
    } finally {
      setActivatingSubscription(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-emerald-100 hover:text-white hover:bg-emerald-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold mb-2">Settings</h1>
          <p className="text-emerald-100 text-sm">Customize your reading experience</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Admin Dashboard Link - Only for Admins */}
          {user?.role === 'admin' && (
            <Card className="dark:bg-slate-800 dark:border-slate-700 border-2 border-purple-200 dark:border-purple-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-700 dark:text-purple-400">
                  <Shield className="w-5 h-5" />
                  Admin Access
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Manage users, payments, and analytics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => navigate('/admin')}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Open Admin Dashboard
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Account Information */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="dark:text-white">Account Information</CardTitle>
              <CardDescription className="dark:text-slate-400">
                Your personal details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-gray-600 dark:text-gray-400">Name</span>
                {isEditingName ? (
                  <div className="flex items-center gap-2 flex-1 max-w-xs">
                    <Input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="h-8 text-sm"
                      placeholder="Enter your name"
                      disabled={isSavingName}
                    />
                    <Button
                      size="sm"
                      onClick={handleSaveName}
                      disabled={isSavingName}
                      className="h-8 w-8 p-0 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEdit}
                      disabled={isSavingName}
                      className="h-8 w-8 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{user.full_name}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsEditingName(true)}
                      className="h-6 w-6 p-0"
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Email</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{user.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Role</span>
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200">
                  {user.subscription_status === 'active' ? 'Subscriber' : 'Registered'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Subscription Status</span>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${
                  user.subscription_status === 'active' 
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    : user.subscription_status === 'trial'
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                    : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                }`}>
                  {user.subscription_status || 'trial'}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Session Management */}
          <Card className="dark:bg-slate-800 dark:border-slate-700 border-2 border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                <Smartphone className="w-5 h-5" />
                Sessions
              </CardTitle>
              <CardDescription className="dark:text-slate-400">
                Manage your active sessions (2-session limit)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Session Count Display */}
              <div className={`p-4 rounded-lg border-2 ${
                deviceCount >= 2 
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700' 
                  : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-semibold ${
                      deviceCount >= 2 
                        ? 'text-red-800 dark:text-red-200' 
                        : 'text-green-800 dark:text-green-200'
                    }`}>
                      {deviceCount >= 2 ? '⚠️ Session Limit Reached' : '✓ Sessions Available'}
                    </p>
                    <p className={`text-xs mt-1 ${
                      deviceCount >= 2 
                        ? 'text-red-700 dark:text-red-300' 
                        : 'text-green-700 dark:text-green-300'
                    }`}>
                      Currently using {deviceCount} {deviceCount === 1 ? 'session' : 'sessions'}
                    </p>
                  </div>
                  <div className={`text-2xl font-bold ${
                    deviceCount >= 2 
                      ? 'text-red-700 dark:text-red-300' 
                      : 'text-green-700 dark:text-green-300'
                  }`}>
                    {deviceCount}/2
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-900 dark:text-blue-200 mb-2">
                  <strong>ℹ️ About Session Limits:</strong>
                </p>
                <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1 ml-4">
                  <li>• You can have up to 2 active sessions simultaneously</li>
                  <li>• If you get "Session limit exceeded" error, clear your sessions below</li>
                  <li>• Useful when you can't access old sessions to log out</li>
                  <li>• You'll need to log in again after clearing sessions</li>
                </ul>
              </div>
              
              <Button
                onClick={handleClearSessions}
                disabled={clearingSessions}
                variant="outline"
                className="w-full border-blue-500 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20"
              >
                {clearingSessions ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                    Clearing Sessions...
                  </>
                ) : (
                  <>
                    <Smartphone className="w-4 h-4 mr-2" />
                    Clear All Sessions
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Security Settings */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="dark:text-white">Security</CardTitle>
              <CardDescription className="dark:text-slate-400">
                Manage your password and account security
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/change-password')}
              >
                Change Password
              </Button>
            </CardContent>
          </Card>

          {/* Subscription Activation - Only show for trial users */}
          {user?.subscription_status === 'trial' && (
            <Card className="dark:bg-slate-800 dark:border-slate-700 border-2 border-emerald-200 dark:border-emerald-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                  <Key className="w-5 h-5" />
                  Activate Subscription
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Enter your activation code to upgrade from Trial to Subscribed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleActivateSubscription} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="activation-code" className="dark:text-slate-200">Activation Code</Label>
                    <Input
                      id="activation-code"
                      type="text"
                      placeholder="ACT-XXXX-XXXX"
                      value={activationCode}
                      onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
                      className="font-mono text-center tracking-wider dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      maxLength={13}
                      data-testid="activation-code-input"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      You will receive this code from the administrator after subscription approval
                    </p>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={activatingSubscription || !activationCode.trim()}
                    data-testid="activate-subscription-btn"
                  >
                    {activatingSubscription ? 'Activating...' : 'Activate Subscription'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Theme Settings */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-slate-100">
                {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                Appearance
              </CardTitle>
              <CardDescription className="dark:text-slate-400">Choose between light and dark mode</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup value={theme} onValueChange={handleThemeChange}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="light" id="light" data-testid="theme-light" />
                  <Label htmlFor="light" className="cursor-pointer">Light Mode</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="dark" id="dark" data-testid="theme-dark" />
                  <Label htmlFor="dark" className="cursor-pointer">Dark Mode</Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Font Size Settings */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-slate-100">
                <Type className="w-5 h-5" />
                Text Size
              </CardTitle>
              <CardDescription className="dark:text-slate-400">Adjust the reading text size</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={fontSize} onValueChange={handleFontSizeChange}>
                <SelectTrigger data-testid="font-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium (Default)</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Notifications Settings */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="dark:text-slate-100">Notifications</CardTitle>
              <CardDescription className="dark:text-slate-400">Receive alerts about content updates and deadlines</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <Label htmlFor="notifications" className="cursor-pointer">
                  Enable push notifications
                </Label>
                <Switch
                  id="notifications"
                  checked={notifications}
                  onCheckedChange={handleNotificationsToggle}
                  data-testid="notifications-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
